import { Helmet } from "react-helmet-async";
import { useParams, Link, Navigate } from "react-router-dom";
import { usePageSeo } from "@/hooks/usePageSeo";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { SEOWrapper } from "@/components/SEOWrapper";
import { CTAFinal } from "@/components/home/CTAFinal";
import { getCaseStudyBySlug, isCaseStudyPublic } from "@/data/caseStudiesData";
import { getPublicCaseTitle } from "@/data/clientDisclosure";
import { KnowledgeConnections } from "@/components/knowledge/KnowledgeConnections";
import { CASE_HUB_MAP } from "@/data/knowledgeGraph";
import {
  InteriorHead,
  Section,
  SectionIndex,
  SectionStrip,
  ContextPanel,
  DeliverableList,
  EvidenceFigure,
  EvidenceSwitcher,
  ActionLink,
  ReadingColumn,
  type SectionRef,
} from "@/components/luminous/interior";
import { routesFor, type Lang } from "@/components/luminous/primitives";
import { getImageDimensions } from "@/data/imageDimensions";
import type { esRoutes } from "@/utils/routes";
import { Calendar } from "lucide-react";

// Evidence images — Amazon Abrasivos
import amazonAbrasivesProducts from "@/assets/case-studies/amazon-abrasives-products.jpg";
import amazonSalesDashboard from "@/assets/case-studies/amazon-sales-dashboard.png";
import amazonCampaignsRoas from "@/assets/case-studies/amazon-campaigns-roas.png";
import amazonInternationalSales from "@/assets/case-studies/amazon-international-sales.png";

// Evidence images — El Niño Aguas Frescas
import elNinoBeveragesDisplay from "@/assets/case-studies/el-nino-beverages-display.jpg";
import elNinoOriginal from "@/assets/case-studies/el-nino-original.gif";
import elNinoBranding from "@/assets/case-studies/el-nino-branding.png";
import elNino2Bowl from "@/assets/case-studies/el-nino-2bowl.jpg";

// Evidence images — El Venado Foods
import venadoGroceryShelf from "@/assets/case-studies/venado-grocery-shelf.jpg";
import venadoOriginal from "@/assets/case-studies/venado-original.png";
import venadoNuevo from "@/assets/case-studies/venado-nuevo.png";
import venadoEcommerce from "@/assets/case-studies/venado-ecommerce.png";

// Evidence images — Bristol Myers Squibb / SICODI
import bmsPharmaTechnology from "@/assets/case-studies/bms-pharma-technology.jpg";
import bmsSicodiProducts from "@/assets/case-studies/bms-sicodi-products.png";
import bmsSicodiUsers from "@/assets/case-studies/bms-sicodi-users.png";
import bmsAvaproSignatures from "@/assets/case-studies/bms-avapro-signatures.png";

const evidenceImageMap: Record<string, string> = {
  "/assets/case-studies/amazon-abrasives-products.jpg": amazonAbrasivesProducts,
  "/assets/case-studies/amazon-sales-dashboard.png": amazonSalesDashboard,
  "/assets/case-studies/amazon-campaigns-roas.png": amazonCampaignsRoas,
  "/assets/case-studies/amazon-international-sales.png": amazonInternationalSales,
  "/assets/case-studies/el-nino-beverages-display.jpg": elNinoBeveragesDisplay,
  "/assets/case-studies/el-nino-original.gif": elNinoOriginal,
  "/assets/case-studies/el-nino-branding.png": elNinoBranding,
  "/assets/case-studies/el-nino-2bowl.jpg": elNino2Bowl,
  "/assets/case-studies/venado-grocery-shelf.jpg": venadoGroceryShelf,
  "/assets/case-studies/venado-original.png": venadoOriginal,
  "/assets/case-studies/venado-nuevo.png": venadoNuevo,
  "/assets/case-studies/venado-ecommerce.png": venadoEcommerce,
  "/assets/case-studies/bms-pharma-technology.jpg": bmsPharmaTechnology,
  "/assets/case-studies/bms-sicodi-products.png": bmsSicodiProducts,
  "/assets/case-studies/bms-sicodi-users.png": bmsSicodiUsers,
  "/assets/case-studies/bms-avapro-signatures.png": bmsAvaproSignatures,
};

// Corrección editorial: en El Venado, los empaques que muestran el logotipo
// de El Venado (no el personaje Doña Chela) se describen como tal. Solo se
// ajusta el pie de imagen; la fuente y el archivo se conservan sin cambios.
const captionOverrides: Record<string, Record<string, { es: string; en: string }>> = {
  "el-venado-foods-branding-ecommerce": {
    "/assets/case-studies/venado-nuevo.png": {
      es: "Nueva identidad: empaques de El Venado Foods con el venado realista rediseñado, aplicado a varias presentaciones de la línea.",
      en: "New identity: El Venado Foods packages with the redesigned realistic deer, applied across several product presentations.",
    },
  },
};

/**
 * Correspondencia verificada entre el nombre del servicio tal y como aparece
 * en el caso y una página de servicio que existe en ambos idiomas. Los
 * servicios sin destino comprobado se conservan como texto: no se inventan
 * destinos ni relaciones.
 */
const RELATED_SERVICE_ROUTES: Record<string, keyof typeof esRoutes> = {
  // ES
  "Amazon SEO": "serviceAmazon",
  "Amazon Ads": "serviceAmazon",
  "Expansión internacional": "serviceInternational",
  "Desarrollo de marca": "serviceBranding",
  "Diseño de logotipos y etiquetas": "serviceBranding",
  "Estrategia de branding cultural": "serviceBranding",
  "Rediseño de identidad visual": "serviceBranding",
  "Desarrollo de e-commerce": "serviceEcommerce",
  "SEO local": "localSeoHub",
  // EN
  "International expansion": "serviceInternational",
  "Brand development": "serviceBranding",
  "Logo and label design": "serviceBranding",
  "Cultural branding strategy": "serviceBranding",
  "Visual identity redesign": "serviceBranding",
  "E-commerce development": "serviceEcommerce",
  "Local SEO": "localSeoHub",
};

const CaseStudyDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const { language, url, esUrl, enUrl, onLanguageChange } = usePageSeo();
  const routes = routesFor(language as Lang);

  const caseStudy = slug ? getCaseStudyBySlug(slug) : undefined;

  if (!caseStudy) {
    return <Navigate to={routes.caseStudies} replace />;
  }

  // P5.5 evidence lock: la URL se mantiene, pero no se publica nombre de
  // cliente, métricas ni testimonio mientras falte autorización/evidencia.
  if (!isCaseStudyPublic(caseStudy)) {
    return (
      <div className="min-h-screen" style={{ background: "var(--v3-mineral)" }}>
        <Helmet>
          <html lang={language} />
          <title>
            {language === "es" ? "Caso pendiente de autorización | Luis ROC" : "Case pending authorization | Luis ROC"}
          </title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <Header language={language} onLanguageChange={onLanguageChange} />
        <main>
          <Section index="—" label={language === "es" ? "Caso de éxito" : "Case study"} tone="mineral">
            <ReadingColumn>
              <h1 className="v3-display" style={{ fontSize: "clamp(1.8rem, 3vw, 2.6rem)" }}>
                {language === "es" ? "Caso pendiente de autorización" : "Case pending authorization"}
              </h1>
              <p className="v3-body mt-6">
                {language === "es"
                  ? "Este caso no se publica hasta contar con autorización del cliente y evidencia verificable de los resultados. Mientras tanto, puedes revisar la metodología de trabajo."
                  : "This case is not published until client authorization and verifiable evidence of results are in place. In the meantime, you can review the working methodology."}
              </p>
              <ActionLink to={routes.methodology}>
                {language === "es" ? "Ver la metodología" : "See the methodology"}
              </ActionLink>
            </ReadingColumn>
          </Section>
        </main>
        <Footer language={language} />
      </div>
    );
  }

  const industry = language === "es" ? caseStudy.industry : caseStudy.industryEn;
  const services = language === "es" ? caseStudy.services : caseStudy.servicesEn;
  const situation = language === "es" ? caseStudy.situation : caseStudy.situationEn;
  const problems = language === "es" ? caseStudy.problems : caseStudy.problemsEn;
  const limitations = language === "es" ? caseStudy.limitations : caseStudy.limitationsEn;
  const steps = caseStudy.steps.map((s) => ({
    title: language === "es" ? s.title : s.titleEn,
    description: language === "es" ? s.description : s.descriptionEn,
  }));
  const results = caseStudy.results.map((r) => ({
    metric: language === "es" ? r.metric : r.metricEn,
    description: language === "es" ? r.description : r.descriptionEn,
  }));
  const resultNote = language === "es" ? caseStudy.resultNote : caseStudy.resultNoteEn;
  const learnings = language === "es" ? caseStudy.learnings : caseStudy.learningsEn;

  const overrides = captionOverrides[caseStudy.slug];
  const evidenceImages = (caseStudy.evidenceImages ?? []).map((img) => {
    const override = overrides?.[img.src];
    const dimensions = getImageDimensions(img.src);
    const alt = language === "es" ? img.alt : img.altEn;
    // Nombre corto del selector: primera oración del alt real, cortada en
    // palabra completa. No se inventa texto nuevo.
    const head = alt.split(/[.:–—]/)[0].trim();
    const name = head.length > 52 ? `${head.slice(0, 52).replace(/\s+\S*$/, "")}…` : head;
    return {
      src: evidenceImageMap[img.src] || img.src,
      width: dimensions?.w,
      height: dimensions?.h,
      alt,
      name,
      caption: override ? override[language] : language === "es" ? img.caption : img.captionEn,
    };
  });

  const isAmazon = caseStudy.slug === "marca-abrasivos-amazon-usa";
  const mainEvidence = isAmazon ? evidenceImages[1] : evidenceImages[0]; // Amazon: dashboard es la principal
  const secondaryEvidence = evidenceImages.filter((_, i) => i !== (isAmazon ? 1 : 0));

  const publicTitle = getPublicCaseTitle(caseStudy.slug, caseStudy.title, language);

  const t = {
    es: {
      hub: "Casos de éxito",
      home: "Inicio",
      contents: "Contenido",
      scope: "Alcance",
      industry: "Industria",
      location: "Ubicación",
      services: "Servicios",
      period: "Periodo",
      sec: {
        context: "Contexto",
        problem: "Problema y decisiones",
        implementation: "Implementación",
        results: "Resultados",
        evidence: "Evidencia",
        related: "Servicios relacionados",
      },
      problemTitle: "Problema y decisiones",
      limitationsLabel: "Límites del proyecto",
      implementationTitle: "Implementación",
      resultsTitle: "Resultados observables",
      evidenceTitle: "Evidencia ampliable",
      relatedTitle: "Servicios relacionados",
      cta: "Solicitar reunión inicial gratuita",
      zoom: "Ampliar",
      evidencePick: "Elegir evidencia",
      amazonSeparation:
        "El resultado citado corresponde al mes 10 del proyecto. Las capturas de evidencia muestran el histórico acumulado de la cuenta (2019–2021) y los reportes de Amazon Ads son de tipo Lifetime: ninguna de las dos prueba, por sí sola, una evolución mes a mes.",
    },
    en: {
      hub: "Case studies",
      home: "Home",
      contents: "Contents",
      scope: "Scope",
      industry: "Industry",
      location: "Location",
      services: "Services",
      period: "Period",
      sec: {
        context: "Context",
        problem: "Problem and decisions",
        implementation: "Implementation",
        results: "Results",
        evidence: "Evidence",
        related: "Related services",
      },
      problemTitle: "Problem and decisions",
      limitationsLabel: "Project limits",
      implementationTitle: "Implementation",
      resultsTitle: "Observable results",
      evidenceTitle: "Expandable evidence",
      relatedTitle: "Related services",
      cta: "Request free initial consultation",
      zoom: "Enlarge",
      evidencePick: "Choose evidence",
      amazonSeparation:
        "The figure cited corresponds to month 10 of the project. The evidence screenshots show the account's accumulated history (2019–2021), and the Amazon Ads reports are Lifetime reports: neither one alone proves a month-by-month evolution.",
    },
  }[language];

  const sections: SectionRef[] = [
    { id: "contexto", label: t.sec.context },
    { id: "problema", label: t.sec.problem },
    { id: "implementacion", label: t.sec.implementation },
    { id: "resultados", label: t.sec.results },
    ...(evidenceImages.length ? [{ id: "evidencia", label: t.sec.evidence }] : []),
    { id: "relacionados", label: t.sec.related },
  ];

  const period = caseStudy.highlightResult
    ? language === "es"
      ? caseStudy.highlightResult.timeline
      : caseStudy.highlightResult.timelineEn
    : undefined;

  const meta = {
    title: `${publicTitle} - ${language === "es" ? "Caso de Éxito" : "Case Study"} | Luis ROC`,
    description:
      language === "es"
        ? `Caso de éxito en ${services.slice(0, 3).join(", ")} para ${industry}.`
        : `Case study in ${services.slice(0, 3).join(", ")} for ${industry}.`,
  };

  return (
    <SEOWrapper
      title={meta.title}
      description={meta.description}
      url={url}
      esUrl={esUrl}
      enUrl={enUrl}
      language={language}
      pageType="article"
      summary={meta.description}
    >
      <div className="min-h-screen" style={{ background: "var(--v3-mineral)" }}>
        <Header language={language} onLanguageChange={onLanguageChange} />

        <InteriorHead
          crumbs={[
            { label: t.home, to: routes.home },
            { label: t.hub, to: routes.caseStudies },
            { label: industry },
          ]}
          eyebrow={industry}
          title={publicTitle}
          definition={situation}
          facts={[
            { label: t.industry, value: industry },
            { label: t.location, value: caseStudy.location },
            { label: t.services, value: services.join(", ") },
            ...(period ? [{ label: t.period, value: period }] : []),
          ]}
          actions={
            <ActionLink to={routes.seoDiagnosis}>
              <Calendar className="h-4 w-4" aria-hidden />
              {t.cta}
            </ActionLink>
          }
        />

        <SectionStrip items={sections} label={t.contents} />

        <main>
          {/* Imagen principal con pie explicativo */}
          {mainEvidence ? (
            <Section id="contexto" index="01" label={t.sec.context} tone="white">
              <div className="grid gap-12 lg:grid-cols-12 lg:gap-14">
                <div className="min-w-0 lg:col-span-8">
                  <EvidenceFigure
                    src={mainEvidence.src}
                    alt={mainEvidence.alt}
                    caption={mainEvidence.caption}
                    width={mainEvidence.width}
                    height={mainEvidence.height}
                    zoomLabel={t.zoom}
                    priority
                  />
                  {isAmazon ? <p className="v3-note mt-4">{t.amazonSeparation}</p> : null}
                </div>
                <ContextPanel title={t.services} className="min-w-0 lg:col-span-4">
                  <ul className="list-none space-y-2 pl-0">
                    {services.map((s) => (
                      <li
                        key={s}
                        className="border-t border-[color:var(--v3-line)] pt-2 text-[0.92rem] leading-relaxed text-[color:var(--v3-ink-soft)] first:border-t-0 first:pt-0"
                      >
                        {s}
                      </li>
                    ))}
                  </ul>
                </ContextPanel>
              </div>
            </Section>
          ) : null}

          {/* Problema y decisiones */}
          <Section id="problema" index="02" label={t.sec.problem} title={t.problemTitle} tone="mineral">
            <div className="grid gap-10 lg:grid-cols-2">
              <ReadingColumn className="max-w-none">
                <ul className="list-none space-y-0 pl-0">
                  {problems.map((p, i) => (
                    <li key={p} className="border-t border-[color:var(--v3-line)] py-3 first:border-t-0">
                      <p className="flex gap-3 text-[0.95rem] leading-relaxed text-[color:var(--v3-ink-soft)]">
                        <span className="v3-micro pt-0.5" style={{ color: "var(--v3-blue)" }}>
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span>{p}</span>
                      </p>
                    </li>
                  ))}
                </ul>
              </ReadingColumn>
              <ContextPanel title={t.limitationsLabel} sticky={false}>
                <ul className="list-none space-y-3 pl-0">
                  {limitations.map((l) => (
                    <li key={l} className="text-[0.92rem] leading-relaxed text-[color:var(--v3-ink-soft)]">
                      {l}
                    </li>
                  ))}
                </ul>
              </ContextPanel>
            </div>
          </Section>

          {/* Implementación con entregables visibles */}
          <Section id="implementacion" index="03" label={t.sec.implementation} title={t.implementationTitle} tone="white">
            <DeliverableList
              items={steps.map((s) => ({ title: s.title, detail: s.description }))}
              columns={2}
            />
          </Section>

          {/* Resultados con periodo y atribución */}
          <Section id="resultados" index="04" label={t.sec.results} title={t.resultsTitle} tone="mineral">
            <div className="grid gap-x-10 gap-y-0 md:grid-cols-2">
              {results.map((r) => (
                <div key={r.metric} className="border-t border-[color:var(--v3-line)] py-4">
                  <p className="text-[0.95rem] font-semibold text-[color:var(--v3-ink)]">{r.metric}</p>
                  <p className="v3-note mt-1.5">{r.description}</p>
                </div>
              ))}
            </div>
            <p className="v3-note mt-8 border-t border-[color:var(--v3-line)] pt-6">{resultNote}</p>
            {isAmazon ? <p className="v3-note mt-3">{t.amazonSeparation}</p> : null}

            {learnings.length ? (
              <div className="mt-10 border-t border-[color:var(--v3-line)] pt-8">
                <p className="v3-micro">{language === "es" ? "Aprendizajes" : "Learnings"}</p>
                <ul className="mt-4 space-y-3">
                  {learnings.map((l) => (
                    <li key={l} className="text-[0.95rem] leading-relaxed text-[color:var(--v3-ink-soft)]">
                      {l}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </Section>

          {/* Evidencia ampliable (secundaria) */}
          {secondaryEvidence.length ? (
            <Section id="evidencia" index="05" label={t.sec.evidence} title={t.evidenceTitle} tone="white">
              {secondaryEvidence.length > 1 ? (
                <EvidenceSwitcher
                  label={t.evidencePick}
                  zoomLabel={t.zoom}
                  items={secondaryEvidence.map((img, i) => ({
                    src: img.src,
                    alt: img.alt,
                    caption: img.caption,
                    width: img.width,
                    height: img.height,
                    name: img.name ?? `${t.sec.evidence} ${String(i + 1).padStart(2, "0")}`,
                  }))}
                />
              ) : (
                <div className="grid gap-10 md:grid-cols-2">
                  {secondaryEvidence.map((img) => (
                    <EvidenceFigure
                      key={img.src}
                      src={img.src}
                      alt={img.alt}
                      caption={img.caption}
                      width={img.width}
                      height={img.height}
                      zoomLabel={t.zoom}
                    />
                  ))}
                </div>
              )}
            </Section>
          ) : null}

          {/* Servicios relacionados */}
          <Section id="relacionados" index="06" label={t.sec.related} title={t.relatedTitle} tone="mineral">
            <div className="flex flex-wrap gap-x-8 gap-y-3">
              {services.map((s) => {
                const routeKey = RELATED_SERVICE_ROUTES[s];
                const to = routeKey ? routes[routeKey] : undefined;
                return to ? (
                  <Link
                    key={s}
                    to={to}
                    className="v3-note underline decoration-[color:var(--v3-line)] underline-offset-4 transition-colors hover:text-[color:var(--v3-blue)] hover:decoration-[color:var(--v3-blue)]"
                    style={{ transitionDuration: "var(--v3-t-control)" }}
                  >
                    {s}
                  </Link>
                ) : (
                  <span key={s} className="v3-note">
                    {s}
                  </span>
                );
              })}
            </div>
          </Section>

          <CTAFinal language={language} />
        </main>

        <KnowledgeConnections hub={CASE_HUB_MAP[caseStudy.slug] ?? "b2b-growth"} language={language} />

        <Footer language={language} />
        <WhatsAppFloat />
      </div>
    </SEOWrapper>
  );
};

export default CaseStudyDetailPage;
